import React, { useEffect, useRef, useState } from 'react';
import { Radio } from 'antd';

import SelectControl from '../SelectControl/SelectControl';
import RelativePositionControl from '../RelativePosition/RelativePosition';
import usePropertiesStore from '../../../stores/usePropertiesStore';
import useAttachedElementsStore from '../../../stores/useAttachedElementsStore';

import styles from './PositioningControl.module.css';

export default function (props) {
  const group = usePropertiesStore((s) => s.selectedGroup);
  const refreshToggle = usePropertiesStore((s) => s.propertiesRefreshTrigger);
  const onPropertyChanged = usePropertiesStore((s) => s.propertyChanged);
  const { attachToElement, detachElement, updateAttachedPosition } = useAttachedElementsStore.getState();
  const [layoutPositioning, setLayoutPositioning] = useState();
  const [attachedTo, setAttachedTo] = useState('x');
  const [attachMode, setAttachMode] = useState('3');
  const [sourceAttachMode, setSourceAttachMode] = useState('1');
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const userLayoutIntentRef = useRef(null);

  useEffect(() => {
    userLayoutIntentRef.current = null;
  }, [group]);

  const attachModeDataSource = [
    { id: '1', name: 'top left' },
    { id: '2', name: 'top center' },
    { id: '3', name: 'top right' },
    { id: '4', name: 'middle left' },
    { id: '5', name: 'middle center' },
    { id: '6', name: 'middle right' },
    { id: '7', name: 'bottom left' },
    { id: '8', name: 'bottom center' },
    { id: '9', name: 'bottom right' },
  ];

  useEffect(() => {
    if (!group) return;
    const stage = group.getStage();
    if (!stage) return;

    const attachId = group.getAttr('attachId');
    let aid;
    if (attachId || attachId === 0) {
      const targets = stage.find('.target');
      aid = targets ? Array.from(targets).find((el) => el.getAttr('attachIds')?.indexOf(attachId) >= 0) : undefined;
    }
    const attachSelectId = aid ? aid._id : 'x';

    let positioningValue;
    if (attachSelectId !== 'x') {
      positioningValue = 'relative';
      userLayoutIntentRef.current = 'relative';
    } else {
      positioningValue = userLayoutIntentRef.current === 'relative' ? 'relative' : 'absolute';
    }

    setLayoutPositioning(positioningValue);
    setAttachedTo(attachSelectId);
    setAttachMode(group.getAttr('attachMode') || '3');
    setSourceAttachMode(group.getAttr('sourceAttachMode') || '1');
    setOffsetX(parseInt(group.getAttr('attachX') || 0));
    setOffsetY(parseInt(group.getAttr('attachY') || 0));

    setDisabled(group.parent && group.parent.hasName('distributed'));

    if (props.setLayoutPositioning) {
      props.setLayoutPositioning(positioningValue);
    }
  }, [group, refreshToggle]); // eslint-disable-line

  const onChangeLayoutPositioning = (e) => {
    const val = e.target.value;
    userLayoutIntentRef.current = val;

    if (val === 'absolute' && attachedTo !== 'x') {
      detachElement(group);
    }

    setLayoutPositioning(val);

    if (props.setLayoutPositioning) {
      props.setLayoutPositioning(val);
    }

    propertyChanged();
  };

  const changeAttachTarget = (val) => {
    if (val !== 'x') {
      if (!group) return;
      const stage = group.getStage();
      if (!stage) return;

      const elementGroups = stage.find('.elementGroup');
      const target = elementGroups ? Array.from(elementGroups).find((e) => e._id === val) : undefined;
      if (!target) return;

      attachToElement(
        group,
        target,
        attachMode,
        sourceAttachMode,
        offsetX,
        offsetY,
      );

      setAttachedTo(val);
      propertyChanged();
    } else {
      detachElement(group);
      setAttachedTo(val);
      propertyChanged();
    }
  };

  const changeAttachPinToMode = (val) => {
    if (val !== 'x') {
      if (!group) return;
      const stage = group.getStage();
      if (!stage) return;

      detachElement(group);
      const elementGroups = stage.find('.elementGroup');
      const target = elementGroups ? Array.from(elementGroups).find((e) => e._id === attachedTo) : undefined;
      if (!target) return;

      attachToElement(
        group,
        target,
        val,
        sourceAttachMode,
        offsetX,
        offsetY,
      );

      setAttachMode(val);
      propertyChanged();

    } else {
      detachElement(group);
      setAttachMode(val);
      propertyChanged();
    }
  };

  const changeAttachPinMode = (val) => {
    if (val !== 'x') {
      if (!group) return;
      const stage = group.getStage();
      if (!stage) return;

      detachElement(group);
      const elementGroups = stage.find('.elementGroup');
      const target = elementGroups ? Array.from(elementGroups).find((e) => e._id === attachedTo) : undefined;
      if (!target) return;

      attachToElement(
        group,
        target,
        attachMode,
        val,
        offsetX,
        offsetY,
      );
      setSourceAttachMode(val);
      propertyChanged();
    } else {
      detachElement(group);
      setSourceAttachMode(val);
      propertyChanged();
    }
  };

  const changeOffsetX = (value) => {
    const val = parseInt(value, 10);
    if (isNaN(val)) {
      return;
    }

    if (attachedTo !== 'x' && updateAttachedPosition) {
      setOffsetX(val);
      updateAttachedPosition(group, val, offsetY, propertyChanged);
    }
  };

  const changeOffsetY = (value) => {
    const val = parseInt(value, 10);
    if (isNaN(val)) {
      return;
    }

    if (attachedTo !== 'x' && updateAttachedPosition) {
      setOffsetY(val);
      updateAttachedPosition(group, offsetX, val, propertyChanged);
    }
  };

  const getViewName = (element) => {
    const elementType = element.getAttr('elementType');
    const elementName = element.getAttr('elementName');

    return elementName || `${elementType} ${element._id}`;
  };

  const propertyChanged = () => {
    if (onPropertyChanged) {
      onPropertyChanged();
    }
  };
  const generateDataSource = (group) => {
    if (!group) return [{ id: 'x', name: 'none' }];
    const stage = group.getStage();
    if (!stage) return [{ id: 'x', name: 'none' }];
    const elementGroups = stage.find('.elementGroup');
    if (!elementGroups) return [{ id: 'x', name: 'none' }];
    const arr = Array.from(elementGroups);

    return arr
      .filter((e) => e !== group)
      .map((e) => ({ id: e._id, name: getViewName(e) }))
      .concat({ id: 'x', name: 'none' });
  };

  const isRelative = layoutPositioning === 'relative';

  return (
    <div>
      <div>
        <Radio.Group
          disabled={disabled}
          onChange={onChangeLayoutPositioning}
          value={layoutPositioning}
          className={styles.radioBlock}
        >
          <Radio value='absolute' className={styles.radioItem}>
            Absolute
          </Radio>
          <Radio value='relative' className={styles.radioItem}>
            Relative
          </Radio>
        </Radio.Group>
      </div>

      {isRelative && (
      <div className={styles.relativePositionBlock}>
        <SelectControl
          disabled={layoutPositioning !== 'relative'}
          label='Relative position to'
          dataSource={generateDataSource(group)}
          onChange={changeAttachTarget}
          value={attachedTo}
        />
        </div>

      )}

      {isRelative && (
        <div className={styles.relativePositionBlock}>
          <RelativePositionControl
            offsetX={offsetX}
            offsetY={offsetY}
            changeOffsetX={changeOffsetX}
            changeOffsetY={changeOffsetY}
            disabled={attachedTo === 'x'}
            dataSource={attachModeDataSource}
            onPinToChange={changeAttachPinToMode}
            onPinByChange={changeAttachPinMode}
            value1={attachMode}
            value2={sourceAttachMode}
          />
        </div>
      )}
    </div>
  );
}
